import { useLocation } from "@reach/router"
import classNames from "classnames"
import React, { useCallback, useContext, useEffect, useState } from "react"

import AuthenticatedMenu from "./AuthenticatedMenu"
import ContentHighlightCTA from "./ContentHighlightCTA"
import { NavigationV2Context } from "./context"
import HamburgerIcon from "./HamburgerIcon"
import MobileNavPanels from "./MobileNavPanels"
import NavHeading, { ContentHighlight, MainLink } from "./NavHeading"
import { useAuth } from "../../../auth/hooks"
import { useGetCartQuantity } from "../../../cart/hooks"
import { CartContext } from "../../../cart/store"
import CartWidget from "../../../cart/V2/CartWidget"
import { CheckoutContext } from "../../../checkout/store"
import { Color } from "../../../constants/V2/color"
import PillButton from "../../elements/V2/Buttons/PillButton"
import YocoLogoIcon from "../../elements/V2/Icons/YocoLogoIcon"
import Section from "../../elements/V2/Section"

import Link from "@components/elements/Link"
import { trackSegmentEvent } from "@utils/analytics"
import { usePageLayoutState } from "@utils/layout"
import { withAppBaseUrl } from "@utils/url"
import { useIsBreakpoint } from "@utils/V2/screen"

type Props = {
  mode?: "light" | "dark"
  hideMobileMenu?: boolean
  isBackgroundTransparent?: boolean
  mainLinks?: MainLink[]
  contentHighlight?: ContentHighlight
  isBannerContents?: boolean
  showCartWidget?: boolean
  primaryButtonText?: string
  primaryButtonLink?: string | YocoCom.YocoLink
  secondaryButtonText: string
  secondaryButtonLink: string | YocoCom.YocoLink
  primaryButtonTrackingEvent?: string
  primaryButtonTrackingEventKey?: string
  primaryButtonTrackingEventValue?: string
  secondaryButtonTrackingEvent?: string
  secondaryButtonTrackingEventKey?: string
  secondaryButtonTrackingEventValue?: string
}

export const MAIN_LINK_CLASSES =
  "flex lg:inline-flex justify-between items-center align-middle mr-8 lg:px-[10px] py-24 lg:pt-[11px] lg:pb-[9px] cursor-pointer bg-transparent lg:rounded-8 lg:hover:bg-white lg:focus-visible:bg-white lg:hover:text-black lg:focus-visible:text-black transition-all duration-200 ease-in-out"

const Navigation = ({
  mode = "light",
  hideMobileMenu = false,
  isBackgroundTransparent = true,
  mainLinks,
  contentHighlight,
  primaryButtonText,
  primaryButtonLink,
  secondaryButtonText,
  secondaryButtonLink,
  isBannerContents = false,
  primaryButtonTrackingEvent,
  primaryButtonTrackingEventKey,
  primaryButtonTrackingEventValue,
  showCartWidget = true,
}: Props) => {
  const navigationContext = useContext(NavigationV2Context)
  const numberOfItemsInCart = useGetCartQuantity()
  const [activeSubMenu, setActiveSubMenu] = useState<number | undefined>()
  const [scrolled, setScrolled] = useState(false)
  const { pathname } = useLocation()
  const isDesktop = useIsBreakpoint("desktop")
  const { isAuthenticated, handleLogin } = useAuth()
  const { isHandlingLogin } = useContext(CheckoutContext)
  const { setNavigationMode, hasAnchorNavigation } = usePageLayoutState()

  const oauthLoginEnabled = process.env.GATSBY_OAUTH_LOGIN === "true"

  // We use this to ensure the menu doesnt flash at SSG step
  useEffect(() => {
    setNavigationMode(mode)
  }, [])

  useEffect(() => {
    if (isDesktop && navigationContext.isMobileMenuOpen) {
      navigationContext.setIsMobileMenuOpen(false)
      setActiveSubMenu(undefined)
    }
  }, [isDesktop, navigationContext])

  // Whenever route changes, close mobile menu
  useEffect(() => {
    if (navigationContext.isMobileMenuOpen) {
      navigationContext.setIsMobileMenuOpen(false)
      setActiveSubMenu(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    const handleScroll = () => {
      const offset = globalThis.scrollY
      const navElement = document.getElementById("navigation")
      if (!navElement) return

      const navTopPosition = navElement.getBoundingClientRect().top
      if (isBannerContents) {
        setScrolled(navTopPosition < 0)
      } else {
        setScrolled(offset > 1)
      }
    }

    // Set initial scroll state
    handleScroll()

    globalThis.addEventListener("scroll", handleScroll, { passive: true })
    return () => globalThis.removeEventListener("scroll", handleScroll)
  }, [isBannerContents])

  const isMenuActive = activeSubMenu !== undefined

  const isMenuOpenOnMobile = navigationContext.isMobileMenuOpen
  const cartContext = useContext(CartContext)

  useEffect(() => {
    if (cartContext.cartPreviewOpen) {
      navigationContext.setIsMobileMenuOpen(false)
      setActiveSubMenu(undefined)
    }
  }, [cartContext.cartPreviewOpen, navigationContext])

  const handleMainLinkHover = useCallback(
    (menuId: number) => {
      if (typeof menuId === "number" && isDesktop) {
        setActiveSubMenu(menuId)
      }
    },
    [isDesktop]
  )

  const handleMainLinkClick = useCallback((menuId: number) => {
    if (typeof menuId === "number") {
      setActiveSubMenu((prev) => (prev === menuId ? undefined : menuId))
    }
  }, [])

  const handleMobileMenuToggle = useCallback(
    (isOpen: boolean) => {
      navigationContext.setIsMobileMenuOpen(isOpen)
      if (!isOpen) {
        setActiveSubMenu(undefined)
      }
    },
    [navigationContext]
  )

  const textColor =
    mode === "light" || scrolled || isMenuActive || isMenuOpenOnMobile
      ? Color.Black
      : Color.White

  const activeContentHighlight =
    mainLinks && activeSubMenu !== undefined
      ? mainLinks[activeSubMenu]?.contentHighlight || contentHighlight
      : contentHighlight

  const shouldShowMobileContentHighlight =
    activeContentHighlight && navigationContext.isMobileMenuOpen

  const supportLink = (
    <Link
      link="https://support.yoco.help/en/"
      className={classNames(
        MAIN_LINK_CLASSES,
        "group gap-x-10 border-b-transparent transition-opacity duration-200 ease-in-out",
        {
          "text-black lg:text-white":
            mode === "dark" && !scrolled && !isMenuActive,
        },
        {
          "text-black": (mode === "light" && scrolled) || isMenuActive,
        }
      )}
    >
      Support
    </Link>
  )

  return (
    <Section
      id="navigation"
      className={classNames(
        "pb-sm left-0 z-30 flex h-[60px] max-h-screen w-screen pt-[9px] transition-colors duration-200 ease-in-out sm:z-30 sm:pt-[14px] md:pt-12 lg:pt-20",
        isBackgroundTransparent && !hasAnchorNavigation
          ? "bg-transparent"
          : mode === "light"
            ? "bg-white"
            : "bg-black",
        isBackgroundTransparent
          ? "sm:bg-transparent"
          : mode === "light"
            ? "sm:bg-white"
            : "sm:bg-black",
        {
          "bg-white sm:bg-white":
            scrolled || isMenuActive || isMenuOpenOnMobile,
        },

        { "fixed top-0": scrolled },
        {
          "shadow-[0_16px_16px_-8px] shadow-charcoal/20 lg:shadow-none":
            navigationContext.isMobileMenuOpen,
        },
        { absolute: !scrolled },
        {
          "!z-50": isMenuActive || isMenuOpenOnMobile,
        }
      )}
      onMouseLeave={() => {
        if (isDesktop) setActiveSubMenu(undefined)
      }}
    >
      <div className="flex max-h-full flex-1 flex-col gap-x-32 lg:flex-row">
        {/* ─── LOGO + HAMBURGER ─--- */}
        <div className="mt-0 flex items-center justify-between sm:-mt-1">
          <Link
            link={`${process.env.GATSBY_PREFIX_PATH || ""}/`}
            aria-label="Yoco Logo"
          >
            <YocoLogoIcon
              className={classNames(
                mode === "light" ||
                  scrolled ||
                  isMenuActive ||
                  isMenuOpenOnMobile
                  ? "fill-black"
                  : "fill-white"
              )}
            />
          </Link>

          <div className="flex items-center gap-x-24 md:gap-x-40 lg:hidden">
            {numberOfItemsInCart > 0 && showCartWidget ? (
              <CartWidget numberOfItems={numberOfItemsInCart} />
            ) : null}

            {!hideMobileMenu ? (
              <HamburgerIcon
                color={textColor}
                mobileMenuOpen={navigationContext.isMobileMenuOpen}
                onToggle={handleMobileMenuToggle}
              />
            ) : null}
          </div>
        </div>
        {/* ─── NAV LINKS ──── */}
        <div
          className={classNames(
            "pointer-events-none -mx-gutter-xs flex max-h-0 flex-1 flex-col justify-between overflow-y-hidden text-body-md font-medium transition-all duration-200 ease-in-out sm:-mx-gutter-sm md:-mx-gutter-md lg:pointer-events-auto lg:-mx-24 lg:mt-0 lg:max-h-screen lg:flex-row lg:items-center lg:overflow-y-visible lg:bg-transparent lg:px-24",
            isMenuOpenOnMobile
              ? "pointer-events-auto min-h-[95svh]"
              : "translate-x-full lg:[transform:unset]"
          )}
        >
          {/* ─ DESKTOP: Always show main links inline, with hover popups ──────── */}
          <div className="flex flex-1 max-lg:hidden lg:flex-row">
            {mainLinks &&
              mainLinks.map((mainLink, index) => (
                <NavHeading
                  mainLink={mainLink}
                  key={`${mainLink.label}-${index}`}
                  className={MAIN_LINK_CLASSES}
                  mode={mode}
                  scrolled={scrolled}
                  isMenuActive={isMenuActive}
                  handleClick={() => handleMainLinkClick(index)}
                  handleHover={() => handleMainLinkHover(index)}
                  handleClose={() => setActiveSubMenu(undefined)}
                  isSubMenuActive={activeSubMenu === index}
                  isMenuOpenOnMobile={isMenuOpenOnMobile}
                />
              ))}
          </div>

          {/* ─ MOBILE ── */}
          {mainLinks && isMenuOpenOnMobile && (
            <MobileNavPanels
              mainLinks={mainLinks}
              mode={mode}
              scrolled={scrolled}
              activeSubMenu={activeSubMenu}
              handleMainLinkClick={handleMainLinkClick}
              handleMainLinkClose={() => {
                setActiveSubMenu(undefined)
              }}
              secondaryButtonText={secondaryButtonText}
              secondaryButtonLink={secondaryButtonLink}
            />
          )}

          {/* ─ DESKTOP RIGHT ITEMS -─ */}
          <div className="relative hidden items-center justify-between gap-x-8 py-[11px] sm:flex lg:py-0">
            {isAuthenticated ? (
              <div className="hidden items-center gap-x-16 lg:flex">
                <AuthenticatedMenu
                  mode={mode}
                  scrolled={scrolled}
                  isMenuActive={isMenuActive}
                />

                {supportLink}

                {numberOfItemsInCart > 0 && showCartWidget && (
                  <CartWidget numberOfItems={numberOfItemsInCart} />
                )}
              </div>
            ) : (
              <div className="hidden items-center lg:flex">
                <Link
                  className={classNames(
                    MAIN_LINK_CLASSES,
                    "border-b-transparent transition-opacity duration-200 ease-in-out",
                    {
                      "text-black lg:text-white":
                        mode === "dark" && !scrolled && !isMenuActive,
                    },
                    {
                      "text-black":
                        (mode === "light" && scrolled) || isMenuActive,
                    }
                  )}
                  onClick={(e) => {
                    if (!oauthLoginEnabled) return

                    e.preventDefault()
                    if (isHandlingLogin) return

                    trackSegmentEvent("nav_login", {
                      page: globalThis.location.pathname,
                    })
                    handleLogin()
                  }}
                  link={withAppBaseUrl("login")}
                >
                  Login
                </Link>

                {supportLink}

                <div className="hidden transition-opacity duration-200 ease-in-out lg:block">
                  {numberOfItemsInCart > 0 && showCartWidget ? (
                    <CartWidget numberOfItems={numberOfItemsInCart} />
                  ) : (
                    !!(primaryButtonText && primaryButtonLink) && (
                      <PillButton
                        size="small"
                        hideIcon
                        color={Color.Blue}
                        overrideTextColor={textColor}
                        style="bordered"
                        text={primaryButtonText}
                        linkUrl={primaryButtonLink}
                        className="transition-all"
                        trackingEvent={primaryButtonTrackingEvent}
                        trackingEventKey={primaryButtonTrackingEventKey}
                        trackingEventValue={primaryButtonTrackingEventValue}
                      />
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile ContentHighlightCTA at Bottom */}
      {shouldShowMobileContentHighlight && (
        <div
          className={classNames(
            "fixed bottom-0 left-0 z-10 w-full border-t border-charcoal/10 bg-white px-20 py-[14px] transition-all duration-200 ease-in-out lg:hidden"
          )}
        >
          <ContentHighlightCTA
            title={activeContentHighlight.title}
            description={activeContentHighlight.description}
            ctaTitle={activeContentHighlight.ctaTitle}
            ctaDescription={activeContentHighlight.ctaDescription}
            ctaLink={activeContentHighlight.ctaLink}
            ctaImage={activeContentHighlight.ctaImage}
            ctaColor={activeContentHighlight.ctaColor}
          />
        </div>
      )}
    </Section>
  )
}

export default Navigation
